import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import { ChannelType, MessageType, type Channel, type Guild, type Member, type Message, type Relationship } from '@/types/api'
import { isEphemeral, isLoading } from './interactions'
import { channelPermissions, threadPermissions, has, P } from './permissions'
import { savedStatusKey, type SavedEntry, type SavedPage } from './savedApi'

const SAVABLE_TYPES = new Set<number>([MessageType.Default, MessageType.Reply, MessageType.ChatInputCommand, MessageType.ContextMenuCommand])
export const canSaveMessage = (m: Message) => SAVABLE_TYPES.has(m.type) && !m.pending && !m.failed && !m.id.startsWith('pending:') && !isEphemeral(m) && !isLoading(m)

export function unavailableSavedEntry(e: SavedEntry): SavedEntry {
  return { id: e.id, channel_id: e.channel_id, message_id: e.message_id, saved_at: e.saved_at, available: false }
}

export function savedEntryVisible(e: SavedEntry, ctx: { userId: string; ready: boolean; guilds: Record<string, Guild>; channels: Record<string, Record<string, Channel>>; members: Record<string, Record<string, Member>>; privateChannels: Record<string, Channel>; relationships: Record<string, Relationship> }): boolean {
  if (!e.available || !e.message || !e.channel || ctx.relationships[e.message.author.id]?.type === 2) return false
  const ch = e.channel, gid = ch.guild_id
  if (!gid) return !ctx.ready || ch.type !== ChannelType.GroupDM || Boolean(ctx.privateChannels[ch.id])
  const guild = ctx.guilds[gid]
  if (!guild) return !ctx.ready
  const live = ctx.channels[gid]?.[ch.id], parent = (live?.parent_id ?? ch.parent_id) ? ctx.channels[gid]?.[live?.parent_id ?? ch.parent_id ?? ''] : undefined
  if (ctx.ready && !live && (ch.type !== ChannelType.PublicThread || !parent)) return false
  const self = ctx.members[gid]?.[ctx.userId] ?? { user: { id: ctx.userId }, roles: [] }
  const perms = ch.type === ChannelType.PublicThread ? threadPermissions(guild, self, parent) : channelPermissions(guild, self, live ?? ch)
  return has(perms, P.VIEW_CHANNEL) && has(perms, P.READ_MESSAGE_HISTORY)
}

/** Даже если исходная переписка не загружена, удаление сразу убирает текст из кеша сохранённого. */
export function savedEntryAfterEvent(e: SavedEntry, t: string, d: { channel_id?: string; message_id?: string; id?: string; ids?: string[] }): SavedEntry | null {
  if (t === 'CHANNEL_DELETE' || t === 'THREAD_DELETE') return e.channel_id === d.id ? unavailableSavedEntry(e) : e
  if (e.channel_id !== d.channel_id || (t === 'MESSAGE_DELETE_BULK' ? !d.ids?.includes(e.message_id) : e.message_id !== (t === 'SAVED_MESSAGE_DELETE' ? d.message_id : d.id))) return e
  if (t === 'SAVED_MESSAGE_DELETE') return null
  if (t === 'MESSAGE_DELETE' || t === 'MESSAGE_DELETE_BULK') return unavailableSavedEntry(e)
  if (t === 'MESSAGE_UPDATE' && e.message) return { ...e, message: { ...e.message, ...d } }
  return e
}

export function applySavedEvent(client: QueryClient, userId: string | undefined, t: string, data: unknown) {
  if (!userId || !['READY', 'SAVED_MESSAGE_CREATE', 'SAVED_MESSAGE_DELETE', 'MESSAGE_UPDATE', 'MESSAGE_DELETE', 'MESSAGE_DELETE_BULK', 'CHANNEL_DELETE', 'THREAD_DELETE'].includes(t)) return
  const prefix = ['saved-messages', userId]
  if (t === 'READY') { void client.invalidateQueries({ queryKey: prefix }); void client.invalidateQueries({ queryKey: ['saved-message-status', userId] }); return }
  const d = data as { channel_id: string; message_id?: string; id?: string; ids?: string[] }
  if (t.startsWith('SAVED_MESSAGE_') && d.message_id) {
    const key = savedStatusKey(userId, d.channel_id, d.message_id)
    void client.cancelQueries({ queryKey: key })
    client.setQueryData(key, { saved: t === 'SAVED_MESSAGE_CREATE' })
  }
  void client.cancelQueries({ queryKey: prefix })
  client.setQueriesData<InfiniteData<SavedPage>>({ queryKey: prefix }, (old) => old && ({ ...old, pages: old.pages.map((page) => ({ ...page, items: page.items.flatMap((e) => { const next = savedEntryAfterEvent(e, t, d); return next ? [next] : [] }) })) }))
  void client.invalidateQueries({ queryKey: prefix })
}
